import React, { useState, useEffect, useRef } from "react";
import { getBaseDocumentProps, siteConfig } from "../renderer/utils/config";
import { HeroSection } from "../renderer/components/HeroSection";
import {
  generateWebpageStructuredData,
  generateBreadcrumbStructuredData,
} from "../renderer/utils/seo";
import { formatPhoneNumber } from "../renderer/utils/formatters";

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
}

// Generate breadcrumb data
const breadcrumbs = [
  { name: "Home", url: "https://shingleguard.ca/" },
  { name: "FAQ", url: "https://shingleguard.ca/faq" },
];

// Define FAQs
const faqs: FAQ[] = [
  {
    id: 1,
    question: "What is shingle restoration?",
    answer:
      "Shingle restoration is a process that revitalizes aging asphalt shingles by applying a specialized formula that restores flexibility, enhances protection, and extends the roof's life by 6-15 years. This is a cost-effective alternative to full roof replacement.",
    category: "process",
  },
  {
    id: 2,
    question: "How long does the restoration process take?",
    answer:
      "The typical restoration process takes 1-2 hours, depending on the size of your roof. This includes preparation, application, and final inspection. We work efficiently while ensuring thorough coverage and quality results.",
    category: "process",
  },
  {
    id: 3,
    question: "Is financing available?",
    answer:
      "Yes, we offer several financing options to make our restoration service more affordable. We partner with trusted financial institutions to provide competitive rates and flexible payment plans. Our team can discuss these options during your consultation.",
    category: "pricing",
  },
  {
    id: 4,
    question: "How do I know if my roof needs restoration or replacement?",
    answer:
      "Our expert assessment will determine the best solution for your roof. Generally, if your shingles are showing signs of aging but aren't severely damaged, our restoration treatment is likely suitable. If your roof has structural issues or is at the end of its lifespan, replacement may be necessary.",
    category: "eligibility",
  },
  {
    id: 5,
    question: "Are you licensed and insured?",
    answer:
      "Absolutely. Shingle Guard is fully licensed, bonded, and insured. We carry comprehensive liability insurance and workers' compensation for all of our employees. We're happy to provide proof of insurance upon request.",
    category: "company",
  },
  {
    id: 6,
    question: "What happens to my landscaping during the process?",
    answer:
      "Absolutely. Our application process is much cleaner than roof replacement, but we still ensure thorough cleanup. We remove any protective coverings, collect any materials, and leave your property in excellent condition.",
    category: "process",
  },
  {
    id: 7,
    question: "What are the best weather conditions for application?",
    answer:
      "The ideal application temperatures are between 50°F and 90°F (10°C to 32°C) with low humidity. In Saskatoon, this typically means applications from late spring through early fall. We schedule based on weather forecasts to ensure optimal application conditions for lasting results.",
    category: "process",
  },
  {
    id: 8,
    question: "Do you offer emergency services?",
    answer:
      "Yes, we provide emergency roofing services for urgent situations. Our team is available to respond to emergency calls and can provide temporary solutions to prevent further damage until we can complete a full restoration.",
    category: "services",
  },
  {
    id: 9,
    question: "What is your warranty?",
    answer:
      "We offer a comprehensive 6-year warranty on our restoration work. This includes both material and labor coverage. Our warranty is transferable to new owners if you sell your property within the warranty period.",
    category: "warranty",
  },
  {
    id: 10,
    question: "How do I schedule an assessment?",
    answer:
      "You can schedule a free assessment by calling us at (306) 500-0000 or filling out our contact form. We typically schedule assessments within 1-2 business days and provide a detailed quote within 24 hours of the assessment.",
    category: "scheduling",
  },
];

// Generate FAQ schema
const generateFAQSchema = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq: FAQ) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return JSON.stringify(faqSchema);
};

// Combine structured data
const generateCombinedStructuredData = () => {
  const pageData = generateWebpageStructuredData({
    title: "Frequently Asked Questions | Shingle Guard",
    description:
      "Find answers to frequently asked questions about our innovative shingle restoration technology that extends roof life by 6-15 years at a fraction of replacement costs.",
    url: `${getBaseDocumentProps().canonicalUrl}/faq`,
    dateModified: "2025-03-02",
  });

  const breadcrumbData = generateBreadcrumbStructuredData(breadcrumbs);
  const faqData = generateFAQSchema();

  // Parse and combine the JSON strings
  return JSON.stringify([
    JSON.parse(pageData),
    JSON.parse(breadcrumbData),
    JSON.parse(faqData),
  ]);
};

export const documentProps = {
  ...getBaseDocumentProps(),
  title: `FAQ | ${getBaseDocumentProps().title}`,
  description:
    "Find answers to frequently asked questions about our innovative shingle restoration technology that extends roof life by 6-15 years at a fraction of replacement costs.",
  keywords:
    "shingle restoration FAQ, roof protection questions, shingle spray cost, Saskatoon shingle protection, roof restoration FAQ, shingle rejuvenation, roof spray cost, shingle protection questions",
  structuredData: generateCombinedStructuredData(),
};

// Separate static content that's safe for server-side rendering
function StaticFAQs() {
  // List of FAQs - same data as in the client component
  const faqs: FAQ[] = [
    {
      id: 1,
      question: "What is shingle restoration?",
      answer:
        "Shingle restoration is a process that revitalizes aging asphalt shingles by applying a specialized formula that restores flexibility, enhances protection, and extends the roof's life by 6-15 years. This is a cost-effective alternative to full roof replacement.",
      category: "process",
    },
    {
      id: 2,
      question: "How long does the restoration process take?",
      answer:
        "The typical restoration process takes 1-2 hours, depending on the size of your roof. This includes preparation, application, and final inspection. We work efficiently while ensuring thorough coverage and quality results.",
      category: "process",
    },
    {
      id: 3,
      question: "Is financing available?",
      answer:
        "Yes, we offer several financing options to make our restoration service more affordable. We partner with trusted financial institutions to provide competitive rates and flexible payment plans. Our team can discuss these options during your consultation.",
      category: "pricing",
    },
    {
      id: 4,
      question: "How do I know if my roof needs restoration or replacement?",
      answer:
        "Our expert assessment will determine the best solution for your roof. Generally, if your shingles are showing signs of aging but aren't severely damaged, our restoration treatment is likely suitable. If your roof has structural issues or is at the end of its lifespan, replacement may be necessary.",
      category: "eligibility",
    },
    {
      id: 5,
      question: "Are you licensed and insured?",
      answer:
        "Absolutely. Shingle Guard is fully licensed, bonded, and insured. We carry comprehensive liability insurance and workers' compensation for all of our employees. We're happy to provide proof of insurance upon request.",
      category: "company",
    },
    {
      id: 6,
      question: "What happens to my landscaping during the process?",
      answer:
        "Absolutely. Our application process is much cleaner than roof replacement, but we still ensure thorough cleanup. We remove any protective coverings, collect any materials, and leave your property in excellent condition.",
      category: "process",
    },
    {
      id: 7,
      question: "What are the best weather conditions for application?",
      answer:
        "The ideal application temperatures are between 50°F and 90°F (10°C to 32°C) with low humidity. In Saskatoon, this typically means applications from late spring through early fall. We schedule based on weather forecasts to ensure optimal application conditions for lasting results.",
      category: "process",
    },
    {
      id: 8,
      question: "Do you offer emergency services?",
      answer:
        "Yes, we provide emergency roofing services for urgent situations. Our team is available to respond to emergency calls and can provide temporary solutions to prevent further damage until we can complete a full restoration.",
      category: "services",
    },
    {
      id: 9,
      question: "What is your warranty?",
      answer:
        "We offer a comprehensive 6-year warranty on our restoration work. This includes both material and labor coverage. Our warranty is transferable to new owners if you sell your property within the warranty period.",
      category: "warranty",
    },
    {
      id: 10,
      question: "How do I schedule an assessment?",
      answer:
        "You can schedule a free assessment by calling us at (306) 500-0000 or filling out our contact form. We typically schedule assessments within 1-2 business days and provide a detailed quote within 24 hours of the assessment.",
      category: "scheduling",
    },
  ];

  return (
    <div>
      <HeroSection
        title="Frequently Asked Questions"
        subtitle="Learn about our innovative shingle restoration technology"
        backgroundImage="/images/faq_static_background.jpg"
      />

      {/* Static version of the search section for initial render */}
      <div className="bg-white py-8 shadow-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl heading-secondary text-dark-green-700 mb-3">
              Have a Question About Shingle Restoration?
            </h2>
            <p className="text-gray-600">
              Search our FAQ for quick answers or contact us for more help
            </p>
          </div>
          <div className="relative max-w-xl mx-auto">
            <input
              type="text"
              placeholder="Search for answers..."
              className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-accent focus:border-accent"
            />
          </div>
        </div>
      </div>

      {/* FAQ Content - static version */}
      <div className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={`faq-${faq.id}`}
                className="bg-gray-50 rounded-lg shadow-md overflow-hidden"
              >
                <div className="px-6 py-5">
                  <div className="text-lg font-medium text-dark-green-700">
                    {faq.question}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Dynamic client-side component with full interactivity
function ClientFAQs() {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(
    {},
  );
  const searchSectionRef = useRef<HTMLDivElement>(null);

  // List of FAQs
  const faqs: FAQ[] = [
    {
      id: 1,
      question: "What is shingle restoration?",
      answer:
        "Shingle restoration is a process that revitalizes aging asphalt shingles by applying a specialized formula that restores flexibility, enhances protection, and extends the roof's life by 6-15 years. This is a cost-effective alternative to full roof replacement.",
      category: "process",
    },
    {
      id: 2,
      question: "How long does the restoration process take?",
      answer:
        "The typical restoration process takes 1-2 hours, depending on the size of your roof. This includes preparation, application, and final inspection. We work efficiently while ensuring thorough coverage and quality results.",
      category: "process",
    },
    {
      id: 3,
      question: "Is financing available?",
      answer:
        "Yes, we offer several financing options to make our restoration service more affordable. We partner with trusted financial institutions to provide competitive rates and flexible payment plans. Our team can discuss these options during your consultation.",
      category: "pricing",
    },
    {
      id: 4,
      question: "How do I know if my roof needs restoration or replacement?",
      answer:
        "Our expert assessment will determine the best solution for your roof. Generally, if your shingles are showing signs of aging but aren't severely damaged, our restoration treatment is likely suitable. If your roof has structural issues or is at the end of its lifespan, replacement may be necessary.",
      category: "eligibility",
    },
    {
      id: 5,
      question: "Are you licensed and insured?",
      answer:
        "Absolutely. Shingle Guard is fully licensed, bonded, and insured. We carry comprehensive liability insurance and workers' compensation for all of our employees. We're happy to provide proof of insurance upon request.",
      category: "company",
    },
    {
      id: 6,
      question: "What happens to my landscaping during the process?",
      answer:
        "Absolutely. Our application process is much cleaner than roof replacement, but we still ensure thorough cleanup. We remove any protective coverings, collect any materials, and leave your property in excellent condition.",
      category: "process",
    },
    {
      id: 7,
      question: "What are the best weather conditions for application?",
      answer:
        "The ideal application temperatures are between 50°F and 90°F (10°C to 32°C) with low humidity. In Saskatoon, this typically means applications from late spring through early fall. We schedule based on weather forecasts to ensure optimal application conditions for lasting results.",
      category: "process",
    },
    {
      id: 8,
      question: "Do you offer emergency services?",
      answer:
        "Yes, we provide emergency roofing services for urgent situations. Our team is available to respond to emergency calls and can provide temporary solutions to prevent further damage until we can complete a full restoration.",
      category: "services",
    },
    {
      id: 9,
      question: "What is your warranty?",
      answer:
        "We offer a comprehensive 6-year warranty on our restoration work. This includes both material and labor coverage. Our warranty is transferable to new owners if you sell your property within the warranty period.",
      category: "warranty",
    },
    {
      id: 10,
      question: "How do I schedule an assessment?",
      answer:
        "You can schedule a free assessment by calling us at (306) 500-0000 or filling out our contact form. We typically schedule assessments within 1-2 business days and provide a detailed quote within 24 hours of the assessment.",
      category: "scheduling",
    },
  ];

  // Initialize filtered FAQs with all FAQs
  const [filteredFaqs, setFilteredFaqs] = useState(faqs);

  // Handle search input changes and direct category clicks
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Set search term when clicking on topic buttons and scroll to search section
  const handleTopicClick = (topic: string) => {
    setSearchTerm(topic);

    // Scroll to the search section
    if (searchSectionRef.current) {
      // Use setTimeout to ensure state update and DOM rendering is complete
      setTimeout(() => {
        // Scroll to the search section
        searchSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        // Find the search input and focus it
        const searchSection = searchSectionRef.current; // Store reference to avoid null checks
        if (searchSection) {
          const searchInput = searchSection.querySelector("input");
          if (searchInput) {
            setTimeout(() => {
              searchInput.focus();

              // Visual feedback - flash the search section
              searchSection.classList.add("bg-accent", "bg-opacity-10");
              setTimeout(() => {
                searchSection.classList.remove("bg-accent", "bg-opacity-10");
              }, 800);
            }, 600); // Wait for scroll to complete
          }
        }
      }, 100);
    }
  };

  // Filter FAQs based on search term - this effect runs on EVERY search term change
  useEffect(() => {
    // Always reset expanded items when search changes
    setExpandedItems({});

    // If search is empty or just whitespace, show all FAQs
    if (!searchTerm || !searchTerm.trim()) {
      console.log("Search is empty, showing all FAQs:", faqs.length);
      setFilteredFaqs(faqs);
      return;
    }

    // Filter based on search term
    const searchTermLower = searchTerm.trim().toLowerCase();
    const results = faqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(searchTermLower) ||
        faq.answer.toLowerCase().includes(searchTermLower) ||
        faq.category.toLowerCase().includes(searchTermLower),
    );

    console.log(`Found ${results.length} results for "${searchTerm}"`);
    console.log(
      "First result:",
      results.length > 0 ? results[0].question : "none",
    );
    setFilteredFaqs(results);
  }, [searchTerm]); // Only depend on searchTerm

  // Function to clear search
  const clearSearch = () => {
    setSearchTerm("");
    // We don't need to set filteredFaqs here as the useEffect will handle it
  };

  // Toggle FAQ item expansion
  const toggleFaq = (id: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <HeroSection
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about our roofing services"
        backgroundImage="/images/faq_background2.jpg"
      />

      {/* Search Section */}
      <div
        ref={searchSectionRef}
        className="bg-gray-50 py-8 transition-colors duration-500"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="relative flex-1 w-full max-w-xl mb-4 md:mb-0">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-4 border border-gray-300 rounded-full shadow-md focus:ring-accent focus:border-accent"
                placeholder="Search questions and answers..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
            <p className="text-dark-green-700 text-sm md:text-base body-text">
              {filteredFaqs.length}{" "}
              {filteredFaqs.length === 1 ? "result" : "results"} found
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* FAQ Items */}
          <div className="space-y-6">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq) => (
                <div
                  key={`faq-${faq.id}-${searchTerm}`}
                  className="bg-gray-50 rounded-lg shadow-md overflow-hidden"
                >
                  <button
                    className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none"
                    onClick={() => toggleFaq(faq.id)}
                    aria-expanded={expandedItems[faq.id]}
                  >
                    <span className="text-lg font-medium text-dark-green-700">
                      {faq.question}
                    </span>
                    <span className="ml-6 flex-shrink-0">
                      <svg
                        className={`h-6 w-6 text-accent transform ${expandedItems[faq.id] ? "rotate-180" : "rotate-0"} transition-transform duration-300`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </button>
                  <div
                    className={`px-6 transition-all duration-300 ease-in-out ${
                      expandedItems[faq.id]
                        ? "max-h-96 opacity-100 pb-5"
                        : "max-h-0 opacity-0 overflow-hidden"
                    }`}
                  >
                    <div className="text-gray-600 text-base body-text">
                      {faq.answer}
                    </div>
                    <div className="mt-3 flex">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-dark-green-100 text-dark-green-800">
                        {faq.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg shadow-md">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-dark-green-700">
                  No results found
                </h3>
                <p className="mt-1 text-gray-500">
                  Try adjusting your search terms or browse all questions.
                </p>
                <button
                  className="mt-4 px-4 py-2 bg-accent text-dark-green-800 rounded-full hover:bg-yellow-500 transition-colors"
                  onClick={clearSearch}
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Still Have Questions Section */}
      <div className="bg-dark-green-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-7/12 p-8 md:p-12">
                <h2 className="text-3xl heading-secondary text-dark-green-700 mb-6">
                  Still Have Questions?
                </h2>
                <p className="text-gray-600 mb-8">
                  Our team is ready to answer any additional questions you might
                  have about our services, pricing, or process.
                </p>
                <div className="space-y-6">
                  <div className="flex">
                    <div className="flex-shrink-0 mt-1">
                      <svg
                        className="h-6 w-6 text-accent"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg text-dark-green-700">Call Us</h3>
                      <p className="mt-1 text-gray-600">
                        Our customer support team is available Monday through
                        Friday, 8am to 6pm.
                      </p>
                      <p className="mt-2 text-accent">
                        {formatPhoneNumber(siteConfig.contactPhone)}
                      </p>
                    </div>
                  </div>
                  <div className="flex">
                    <div className="flex-shrink-0 mt-1">
                      <svg
                        className="h-6 w-6 text-accent"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg text-dark-green-700">Email Us</h3>
                      <p className="mt-1 text-gray-600">
                        Send us your questions and we'll respond within 24
                        hours.
                      </p>
                      <p className="mt-2 text-accent">
                        {siteConfig.contactEmail}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <a
                    href="/contact"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base body-text rounded-full shadow-md text-dark-green-800 bg-accent hover:bg-yellow-500 transition-all duration-300 hover:scale-105 hover:-translate-y-1"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
              <div className="md:w-5/12 bg-dark-green-700 p-8 md:p-12 text-white">
                <h3 className="text-xl heading-tertiary mb-6">Common Topics</h3>
                <ul className="space-y-4">
                  <li>
                    <button
                      className="flex items-center text-gray-100 hover:text-accent transition-colors"
                      onClick={() => handleTopicClick("process")}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Roof Restoration Process
                    </button>
                  </li>
                  <li>
                    <button
                      className="flex items-center text-gray-100 hover:text-accent transition-colors"
                      onClick={() => handleTopicClick("eligibility")}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Eligibility for Restoration
                    </button>
                  </li>
                  <li>
                    <button
                      className="flex items-center text-gray-100 hover:text-accent transition-colors"
                      onClick={() => handleTopicClick("warranty")}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Warranty Information
                    </button>
                  </li>
                  <li>
                    <button
                      className="flex items-center text-gray-100 hover:text-accent transition-colors"
                      onClick={() => handleTopicClick("pricing")}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Pricing and Financing
                    </button>
                  </li>
                  <li>
                    <button
                      className="flex items-center text-gray-100 hover:text-accent transition-colors"
                      onClick={() => handleTopicClick("safety")}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Safety Considerations
                    </button>
                  </li>
                  <li>
                    <button
                      className="flex items-center text-gray-100 hover:text-accent transition-colors"
                      onClick={() => handleTopicClick("maintenance")}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Roof Maintenance
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-dark-green-700 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/roof_closeup.jpg')`,
            opacity: "0.1",
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl heading-secondary mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto">
              Contact Shingle Guard today to discuss your roofing needs and
              experience the difference quality makes.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/contact"
                className="px-8 py-4 bg-accent text-dark-green-800 body-text rounded-full shadow-md hover:bg-yellow-500 transition-all duration-300 hover:scale-105 hover:-translate-y-1 text-center"
              >
                Get Free Estimate
              </a>
              <a
                href="/how-it-works"
                className="px-8 py-4 bg-dark-green-600 text-white body-text rounded-full shadow-md hover:bg-dark-green-500 transition-all duration-300 hover:scale-105 hover:-translate-y-1 text-center"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main component that renders the appropriate version
export function Page() {
  // Check if we're on the server
  if (typeof window === "undefined") {
    // Server-side: render the static version
    return <StaticFAQs />;
  }

  // Client-side: use the dynamic version after initial render
  return <ClientFAQs />;
}

export function onBeforeRender() {
  // For static generation, we need to return all the props that will be used during build time
  const pageProps = {
    isStatic: true,
    lastBuildTime: new Date().toISOString(),
  };

  return {
    pageContext: {
      pageProps,
      documentProps,
    },
  };
}
